import type React from "react";
import { Fragment, useContext, useMemo, useRef, useState } from "react";

import { faFilesMedical } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { useTheme } from "styled-components";
import invariant from "ts-invariant";

import { useGetHealthReportsV2Query } from "api/hooks/useGetHealthReportsV2Query";
import { Roles } from "api/schemas/shared";
import type { HealthReportV2Response } from "api/types/GetHealthReportsV2Response";
import { useProfileContext } from "contexts/ProfileContext";
import { Notification } from "shared/atoms/Notification";
import { CurrentPatientContext } from "utils/contexts";
import { useLocalizedDate } from "utils/date";
import { userIsPartner } from "utils/profile/profileHelper";

import { HealthReportsHeaderV2 } from "../HealthReportsHeader/HealthReportsHeaderV2";

import { HealthReportsGridSkeleton } from "./HealthReportsGrid";
import {
  AnswerCellContainer,
  ChangeIndicator,
  GridContainer,
  HeaderCell,
  QuestionCellContainer,
  QuestionsHeaderCell,
} from "./HealthReportsGrid/styles";

const PROM_KEYS = [
  "eq5d5l_uk",
  "fihoa",
  "hoos12",
  "hoos12_func",
  "hoos12_pain",
  "hoos12_qol",
  "koos12",
  "koos12_func",
  "koos12_pain",
  "koos12_qol",
  "npq",
  "rmdq",
  "spadi",
  "spadi_func",
  "spadi_pain",
] as const;

const sortByWeekNumber = (a: HealthReportV2Response, b: HealthReportV2Response) => a.week_number - b.week_number;

const promChanged = (reports: HealthReportV2Response[], promKey: string): boolean => {
  if (reports.length < 2) return false;
  const values = reports.map(r => r.prom[promKey as keyof typeof r.prom]);
  const firstDefined = values.find(v => v !== undefined);
  if (firstDefined === undefined) return false;
  return values.some(v => v !== undefined && v !== firstDefined);
};

const normalizeResponse = (value: string | number | boolean | string[]): string | number | boolean | string[] => {
  if (value === "true") return true;
  if (value === "false") return false;
  if (typeof value === "string" && value.startsWith("[")) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed as string[];
    } catch {
      // not JSON, return as-is
    }
  }
  return value;
};

const responseChanged = (reports: HealthReportV2Response[], question: string): boolean => {
  if (reports.length < 2) return false;
  const values = reports.map(r => {
    const match = r.responses.find(resp => resp.question === question);
    return match?.response !== undefined ? normalizeResponse(match.response) : undefined;
  });
  const firstDefined = values.find(v => v !== undefined);
  if (firstDefined === undefined) return false;
  return values.some(v => {
    if (v === undefined) return false;
    if (Array.isArray(firstDefined) && Array.isArray(v)) {
      return JSON.stringify(firstDefined) !== JSON.stringify(v);
    }
    return v !== firstDefined;
  });
};

export const HealthReportsV2: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { profile } = useProfileContext();
  const { patient, previousTreatmentId } = useContext(CurrentPatientContext);
  const { patientId } = useParams<{ patientId: string }>();
  const { format, parseISO } = useLocalizedDate();

  const [selected, setSelected] = useState<HealthReportV2Response[]>([]);
  const [hideUnmodifiedRows, setHideUnmodifiedRows] = useState(false);
  const initializedForPatientId = useRef<number | null>(null);

  invariant(profile, "Profile must be defined");

  const {
    data: reports = [],
    error,
    isLoading,
  } = useGetHealthReportsV2Query(
    {
      patientId: patient.id,
      role: userIsPartner(profile) ? Roles.ClinicPartner : Roles.Therapist,
      treatment_id: previousTreatmentId,
    },
    {
      onSuccess(data) {
        if (initializedForPatientId.current === patient.id || data.length === 0) return;
        initializedForPatientId.current = patient.id;
        const sorted = [...data].sort(sortByWeekNumber);
        setSelected(sorted.length === 1 ? [sorted[0]] : sorted.slice(-2));
      },
    }
  );

  const sortedSelected = useMemo(() => [...selected].sort(sortByWeekNumber), [selected]);

  const allPromKeys = useMemo(() => {
    const keySet = new Set<string>();
    sortedSelected.forEach(report => {
      Object.keys(report.prom).forEach(key => {
        keySet.add(key);
      });
    });
    return PROM_KEYS.filter(key => keySet.has(key));
  }, [sortedSelected]);

  const questions = useMemo(() => {
    const questionSet = new Set<string>();
    sortedSelected.forEach(report => {
      report.responses.forEach(r => {
        questionSet.add(r.question);
      });
    });
    return Array.from(questionSet);
  }, [sortedSelected]);

  const formatValue = (value: unknown): string => {
    if (value === true || value === "true") return t("buttons.yes");
    if (value === false || value === "false") return t("buttons.no");
    return String(value);
  };

  const formatAnswer = (value: string | number | boolean | string[]): React.ReactNode => {
    if (Array.isArray(value)) {
      return value.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={i}>
          {formatValue(item)}
          {i < value.length - 1 && <br />}
        </span>
      ));
    }

    if (typeof value === "boolean" || value === "true" || value === "false") {
      return formatValue(value);
    }

    if (typeof value === "string" && value.startsWith("[")) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map((item: unknown, i: number) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i}>
              {formatValue(item)}
              {i < parsed.length - 1 && <br />}
            </span>
          ));
        }
      } catch {
        // not JSON, return as-is
      }
    }

    return value;
  };

  const getAnswer = (
    report: HealthReportV2Response,
    question: string
  ): string | number | boolean | string[] | undefined => {
    const match = report.responses.find(r => r.question === question);
    return match?.response;
  };

  const changedPromKeys = useMemo(() => {
    const changed = new Set<string>();
    allPromKeys.forEach(key => {
      if (promChanged(sortedSelected, key)) changed.add(key);
    });
    return changed;
  }, [allPromKeys, sortedSelected]);

  const changedQuestions = useMemo(() => {
    const changed = new Set<string>();
    questions.forEach(question => {
      if (responseChanged(sortedSelected, question)) changed.add(question);
    });
    return changed;
  }, [questions, sortedSelected]);

  const visiblePromKeys = useMemo(
    () => allPromKeys.filter(key => !hideUnmodifiedRows || changedPromKeys.has(key)),
    [allPromKeys, hideUnmodifiedRows, changedPromKeys]
  );

  const visibleQuestions = useMemo(
    () => questions.filter(q => !hideUnmodifiedRows || changedQuestions.has(q)),
    [questions, hideUnmodifiedRows, changedQuestions]
  );

  const changeCallback = (selectedIds: number[]) => {
    const selectedReports = reports.filter(r => selectedIds.includes(r.id));
    setSelected(selectedReports);
  };

  if (isLoading) return <HealthReportsGridSkeleton columnCount={2} rowCount={10} />;

  if (error) {
    return (
      <Box my={5} mx="auto" maxWidth={400}>
        <Notification variant="danger">{t("errors.generic")}</Notification>
      </Box>
    );
  }

  if (reports.length === 0) {
    return (
      <Stack spacing={2} my={5} sx={{ alignItems: "center", justifyContent: "center" }}>
        <FontAwesomeIcon icon={faFilesMedical} size="3x" color={theme.colors.redesign.db40} />
        <Typography color="textSecondary">{t("patients.health_reports.no_reports_detail")}</Typography>
        <Typography color="textSecondary">
          {t("patients.health_reports.no_reports_detail_v2_hint")}{" "}
          <Link to={`/patients/${patientId}/information/health-reports`}>{t("patients.health_reports.here")}</Link>.
        </Typography>
      </Stack>
    );
  }

  const selectedIds = selected.map(r => r.id);

  return (
    <>
      <HealthReportsHeaderV2
        selected={selectedIds}
        reportList={reports}
        changeCallback={changeCallback}
        rowsCallback={setHideUnmodifiedRows}
      />
      <GridContainer
        columnCount={sortedSelected.length}
        data-testid="health-reports-v2-grid"
        sx={{ maxHeight: "none", overflow: "visible" }}
      >
        <Box display="contents">
          <QuestionsHeaderCell>
            <Typography variant="subtitle2">{t("patients.health_reports.questions")}</Typography>
          </QuestionsHeaderCell>

          {sortedSelected.map(report => (
            <HeaderCell key={report.id}>
              <Stack spacing={0.5} flexDirection="row" flexWrap="wrap" justifyContent="space-between" flex={1}>
                <Typography variant="subtitle2">
                  {t("patients.health_reports.questionnaire_v2", { number: report.week_number })}
                </Typography>
                <Typography variant="subtitle1">{format(parseISO(report.created_at), "P")}</Typography>
              </Stack>
            </HeaderCell>
          ))}
        </Box>

        {visiblePromKeys.map((promKey, rowIndex) => {
          const hasChanged = changedPromKeys.has(promKey);

          return (
            <Fragment key={promKey}>
              <QuestionCellContainer rowIndex={rowIndex}>
                <Typography variant="body_bold">{t(`patients.health_reports.prom_labels.${promKey}`)}</Typography>
              </QuestionCellContainer>

              {sortedSelected.map(report => {
                const value = report.prom[promKey as keyof typeof report.prom];
                const isMissing = value === undefined;

                return (
                  <AnswerCellContainer key={`${promKey}-${report.id}`} rowIndex={rowIndex} hasChanged={hasChanged}>
                    {hasChanged && <ChangeIndicator data-testid="change-indicator" />}
                    <Typography
                      variant="body_thin"
                      fontWeight={600}
                      sx={{
                        color: isMissing ? theme.colors.redesign.db40 : theme.colors.redesign.db100,
                      }}
                    >
                      {isMissing ? t("patients.health_reports.not_available") : value}
                    </Typography>
                  </AnswerCellContainer>
                );
              })}
            </Fragment>
          );
        })}

        {visibleQuestions.map((question, idx) => {
          const hasChanged = changedQuestions.has(question);
          const rowIndex = visiblePromKeys.length + idx;

          return (
            <Fragment key={question}>
              <QuestionCellContainer rowIndex={rowIndex}>
                <Typography>{question}</Typography>
              </QuestionCellContainer>

              {sortedSelected.map(report => {
                const answer = getAnswer(report, question);
                const isNotAsked = answer === undefined;

                return (
                  <AnswerCellContainer key={`${question}-${report.id}`} rowIndex={rowIndex} hasChanged={hasChanged}>
                    {hasChanged && <ChangeIndicator data-testid="change-indicator" />}
                    <Typography
                      variant="body_thin"
                      sx={{
                        color: isNotAsked ? theme.colors.redesign.db40 : theme.colors.redesign.db100,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {isNotAsked ? t("patients.health_reports.questions_content.not_asked") : formatAnswer(answer)}
                    </Typography>
                  </AnswerCellContainer>
                );
              })}
            </Fragment>
          );
        })}
      </GridContainer>
    </>
  );
};
